import React, { useState, useEffect, useRef, useContext } from "react";
import { ChevronUp } from "lucide-react";
import API from "../../../../config/axiosConfig";
import { UserContext } from "../../../../context/UserContext";
import { useTrainingTopics, useTrainingTests } from "../../hooks";
import { useImageHandling } from "../../utils/imageHelpers";
import UploadErrors from "../../../../components/UploadErrors";
import "./TrainingCreation.css";
import { FloatingButton, TrainingSteps, StepOne, StepTwo, StepThree } from "../../components";

const TrainingCreation = ({
  onCancel,
  onSuccess,
  editingTestId = null,
  selectedHospitalId: initialHospitalId = "",
  selectedTopicId: initialTopicId = "",
  initialTestName = "",
  isNewTopic = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedHospitalId, setSelectedHospitalId] = useState(initialHospitalId);
  const [selectedTopicId, setSelectedTopicId] = useState(initialTopicId);
  const [topicName, setTopicName] = useState("");
  const [testName, setTestName] = useState(initialTestName || "");
  const [questions, setQuestions] = useState([
    { question: "", answers: ["", "", "", ""], correct: 0, explanation: "" }
  ]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTestName, setEditedTestName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  
  const testNameEditableInputRef = useRef(null);
  const questionFormRefs = useRef([]);
  const [scrollToRef, setScrollToRef] = useState(null);
  const errorRef = useRef(null);

  // The save button sits at the bottom of a long question list while this
  // banner lives in the header, so a failed save was easy to miss entirely.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  useEffect(() => {
    questionFormRefs.current = Array(questions.length)
      .fill(null)
      .map((_, i) => questionFormRefs.current[i] || React.createRef());
  }, [questions.length]);


  const {
    images: questionImages,
    previews: questionImagePreviews,
    updateImage: updateQuestionImage,
    removeImage: removeQuestionImage,
    setPreviewUrl: setQuestionPreviewUrl,
    clearAllImages: clearAllQuestionImages,
    updateAllImagesAndPreviews: updateAllQuestionImagesAndPreviews
  } = useImageHandling();

  const {
    images: explanationImages,
    previews: explanationImagePreviews,
    updateImage: updateExplanationImage,
    removeImage: removeExplanationImage,
    setPreviewUrl: setExplanationPreviewUrl,
    clearAllImages: clearAllExplanationImages,
    updateAllImagesAndPreviews: updateAllExplanationImagesAndPreviews
  } = useImageHandling();
    
  const { user, hospitals: allHospitals, permittedApps } = useContext(UserContext);
  const [hospitals, setHospitals] = useState([]);
  const [hospitalsLoading, setHospitalsLoading] = useState(true);

  useEffect(() => {
    if (allHospitals && allHospitals.length > 0 && user && permittedApps) {
      const trainingApp = permittedApps.find(app => app.key === 'TRAINING');
      let editableHospitals = [];

      if (user.isSuperAdmin) {
        editableHospitals = [...allHospitals];
      } else if (trainingApp) {
        const permittedHospitalIds = trainingApp.admins
          .filter(admin => admin.user._id === user._id)
          .map(admin => admin.hospital._id);
        
        editableHospitals = allHospitals.filter(hospital =>
          permittedHospitalIds.includes(hospital._id)
        );
      }
      
      setHospitals(editableHospitals);
      setHospitalsLoading(false);
    }
  }, [allHospitals, user, permittedApps]);

  const { topics, loading: topicsLoading, refreshTopics } = useTrainingTopics(selectedHospitalId);
  const { tests, loading: testsLoading } = isNewTopic ?
    { tests: [], loading: false } :
    useTrainingTests(selectedTopicId, selectedHospitalId);

  const clearAllImageStates = () => {
    clearAllQuestionImages();
    clearAllExplanationImages();
  };

  useEffect(() => {
    if (scrollToRef) {
      console.log("Scroll useEffect triggered. scrollToRef:", scrollToRef, "scrollToRef.current:", scrollToRef.current); // DEBUG
      
      const attemptScroll = (refToScroll) => {
        if (refToScroll.current && typeof refToScroll.current.scrollIntoView === 'function') {
          console.log("Attempting to scroll to:", refToScroll.current); // DEBUG
          refToScroll.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof refToScroll.current.focus === 'function') {
            refToScroll.current.focus({ preventScroll: true });
          } else {
            const focusableChild = refToScroll.current.querySelector('input, textarea, button');
            if (focusableChild && typeof focusableChild.focus === 'function') {
              focusableChild.focus({ preventScroll: true });
            }
          }
          return true; // Success
        }
        return false; // Failed
      };

      let rafId;
      let timeoutId;

      rafId = requestAnimationFrame(() => {
        if (attemptScroll(scrollToRef)) {
          console.log("Scroll successful in RAF."); // DEBUG
          setScrollToRef(null);
        } else {
          console.warn("Scroll target ref.current not available in RAF. Retrying with setTimeout(0). Ref:", scrollToRef); // DEBUG
          timeoutId = setTimeout(() => {
            if (attemptScroll(scrollToRef)) {
              console.log("Scroll successful in setTimeout(0)."); // DEBUG
            } else {
              console.error("Scroll target ref.current still not available after RAF and setTimeout(0). Ref:", scrollToRef); // DEBUG
            }
            setScrollToRef(null);
          }, 0);
        }
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }
  }, [scrollToRef]);

  useEffect(() => { 
    const loadTestData = async () => {
      if (!editingTestId || !selectedHospitalId || !selectedTopicId || isNewTopic) return;
      setIsLoading(true); setError(null);
      try {
        const testData = tests.find(t => t.id === editingTestId || t._id === editingTestId);
        if (testData) {
          setTestName(testData.title || "");
          setQuestions(
            testData.questions?.length > 0
              ? testData.questions.map(q => ({
                  question: q.question || "",
                  answers: q.answers?.length > 0 ? q.answers : ["", "", "", ""],
                  correct: q.correct ?? 0,
                  explanation: q.explanation || "",
                  questionImage: q.questionImage || null,
                  explanationImage: q.explanationImage || null,
                }))
              : [{ question: "", answers: ["", "", "", ""], correct: 0, explanation: "" }]
          );
          clearAllImageStates();
          const imgIndices = new Set();
          testData.questions?.forEach((q, i) => { if (q.questionImage?.url || q.explanationImage?.url) imgIndices.add(i); });
          const idxMap = {};
          [...imgIndices].sort((a,b)=>a-b).forEach((idx, newIdx) => idxMap[idx] = newIdx);
          testData.questions?.forEach((q, i) => {
            const newIdx = idxMap[i] ?? i;
            if (q.questionImage?.url) setQuestionPreviewUrl(newIdx, q.questionImage.url);
            if (q.explanationImage?.url) setExplanationPreviewUrl(newIdx, q.explanationImage.url);
          });
        } else {
          setQuestions([{ question: "", answers: ["", "", "", ""], correct: 0, explanation: "" }]);
           console.log(`Test with ID: ${editingTestId} not found in pre-loaded tests`);
        }
      } catch (e) { console.error("Load test data error:", e); }
      finally { setIsLoading(false); }
    };
    loadTestData();
  }, [editingTestId, selectedHospitalId, selectedTopicId, isNewTopic, tests]);

  useEffect(() => { 
    if (!hospitalsLoading && hospitals.length === 1 && !selectedHospitalId) setSelectedHospitalId(hospitals[0]._id);
  }, [hospitals, hospitalsLoading, selectedHospitalId]);

  useEffect(() => { 
    if ((editingTestId || initialTestName) && selectedHospitalId && ((selectedTopicId && !isNewTopic) || (isNewTopic && !selectedTopicId))) {
      setCurrentStep(3);
    }
  }, [editingTestId, initialTestName, selectedHospitalId, selectedTopicId, isNewTopic]);

  const handleQuestionImageChange = (questionIndex, e) => { 
    if (e.target.files && e.target.files.length > 0) updateQuestionImage(questionIndex, e.target.files[0]); 
  };
  const handleExplanationImageChange = (questionIndex, e) => { 
    if (e.target.files && e.target.files.length > 0) updateExplanationImage(questionIndex, e.target.files[0]); 
  };
  const handleRemoveQuestionImage = (questionIndex) => { 
    const updatedQuestions = [...questions]; 
    if(updatedQuestions[questionIndex]){updatedQuestions[questionIndex].deleteQuestionImage=true;setQuestions(updatedQuestions);} 
    removeQuestionImage(questionIndex);
  };
  const handleRemoveExplanationImage = (questionIndex) => { 
    const updatedQuestions = [...questions]; 
    if(updatedQuestions[questionIndex]){updatedQuestions[questionIndex].deleteExplanationImage=true;setQuestions(updatedQuestions);} 
    removeExplanationImage(questionIndex);
  };
  const handleHospitalSelect = (hospitalId) => { 
    setSelectedHospitalId(hospitalId); 
    if(!isNewTopic) setSelectedTopicId(""); 
  };
  const handleTopicSelect = (topicId) => setSelectedTopicId(topicId);
  const setTestNameProxy = (value) => { 
    setTestName(value); 
    if(formErrors.testName)setFormErrors(p=>({...p,testName:undefined})); 
  };
  const setEditedTestNameProxy = (value) => { 
    setEditedTestName(value); 
    if(formErrors.testName&&isEditingTitle)setFormErrors(p=>({...p,testName:undefined})); 
  };
  const handleNextStep = () => { 
    if (currentStep === 1 && !selectedHospitalId) { alert("אנא בחר בית חולים"); return; }
    if (currentStep === 1 && isNewTopic && !topicName.trim()) { alert("אנא הכנס שם נושא"); return; }
    if (currentStep === 1 && !isNewTopic && !selectedTopicId) { alert("אנא בחר נושא"); return; }
    if (currentStep === 2 && !testName.trim()) { alert("אנא הכנס שם מבחן"); return; }
    setCurrentStep(p=>p+1); 
  };
  const handlePreviousStep = () => { 
    if(currentStep===3&&initialTestName){handleCancel();return;} 
    if(currentStep>1)setCurrentStep(p=>p-1); 
  };
  const handleAddQuestion = () => { 
    setQuestions(q=>[...q, {question:"",answers:["","","",""],correct:0,explanation:""}]); 
  };
  
  const handleQuestionChange = (questionIndex, field, value) => {
    const updatedQuestions=[...questions]; const q=updatedQuestions[questionIndex];
    if(field==="answers"){if(typeof value==='object'&&value.hasOwnProperty('index')&&value.hasOwnProperty('text'))q.answers[value.index]=value.text;}
    else if(field==="correct")q.correct=value; else q[field]=value;
    setQuestions(updatedQuestions);
    setFormErrors(prevErrors=>{ const newErrors=JSON.parse(JSON.stringify(prevErrors)); if(newErrors.questions?.[questionIndex]){
      if((field==="question"||field==="explanation")&&newErrors.questions[questionIndex][field])delete newErrors.questions[questionIndex][field];
      else if((field==="answers"||field==="correct")&&newErrors.questions[questionIndex].answersGeneral){if(q.answers.every(a=>a?.trim()))delete newErrors.questions[questionIndex].answersGeneral;}
      if(Object.keys(newErrors.questions[questionIndex]).length===0)delete newErrors.questions[questionIndex];
      if(Object.keys(newErrors.questions).length===0)delete newErrors.questions;
    } return newErrors;});
  };

  const handleDeleteQuestion = (indexToDelete) => {
    if (questions.length <= 1) { alert("לא ניתן למחוק את השאלה האחרונה"); return; }
    const currentState = {
      questions: [...questions], questionImages: {...questionImages}, questionPreviews: {...questionImagePreviews},
      explanationImages: {...explanationImages}, explanationPreviews: {...explanationImagePreviews}
    };
    const updatedQuestions = [...questions]; updatedQuestions.splice(indexToDelete, 1);
    const newQI = {}, newQP = {}, newEI = {}, newEP = {};
    Object.keys(currentState.questionImages).forEach(idx => { const i=parseInt(idx); if(i===indexToDelete)return; const newIdx=i>indexToDelete?i-1:i; newQI[newIdx]=currentState.questionImages[i]; });
    Object.keys(currentState.questionPreviews).forEach(idx => { const i=parseInt(idx); if(i===indexToDelete)return; const newIdx=i>indexToDelete?i-1:i; newQP[newIdx]=currentState.questionPreviews[i]; });
    Object.keys(currentState.explanationImages).forEach(idx => { const i=parseInt(idx); if(i===indexToDelete)return; const newIdx=i>indexToDelete?i-1:i; newEI[newIdx]=currentState.explanationImages[i]; });
    Object.keys(currentState.explanationPreviews).forEach(idx => { const i=parseInt(idx); if(i===indexToDelete)return; const newIdx=i>indexToDelete?i-1:i; newEP[newIdx]=currentState.explanationPreviews[i]; });
    setQuestions(updatedQuestions);

    // Directly update all image and preview states.
    // The updateAll... functions from useImageHandling will manage
    // revoking old blob URLs and setting the new state atomically.
    updateAllQuestionImagesAndPreviews(newQI, newQP);
    updateAllExplanationImagesAndPreviews(newEI, newEP);
    setFormErrors(prevErrors => { 
        const newErrors = JSON.parse(JSON.stringify(prevErrors));
        if (newErrors.questions) {
            const updatedQErrors = {};
            Object.keys(newErrors.questions).forEach(errIdxStr => {
                const errIdx = parseInt(errIdxStr); if (errIdx === indexToDelete) return;
                const newErrIdx = errIdx > indexToDelete ? errIdx - 1 : errIdx;
                updatedQErrors[newErrIdx] = newErrors.questions[errIdxStr];
                if (updatedQErrors[newErrIdx].question?.includes(`שאלה ${errIdx + 1}`)) updatedQErrors[newErrIdx].question = updatedQErrors[newErrIdx].question.replace(`שאלה ${errIdx + 1}`, `שאלה ${newErrIdx + 1}`);
                if (updatedQErrors[newErrIdx].answersGeneral?.includes(`שאלה ${errIdx + 1}`)) updatedQErrors[newErrIdx].answersGeneral = updatedQErrors[newErrIdx].answersGeneral.replace(`שאלה ${errIdx + 1}`, `שאלה ${newErrIdx + 1}`);
            });
            newErrors.questions = updatedQErrors;
            if (Object.keys(newErrors.questions).length === 0) delete newErrors.questions;
        }
        return newErrors;
    });
  };
  const handleAddAnswer = (questionIndex) => { 
    const updatedQuestions=[...questions];
    updatedQuestions[questionIndex].answers.push("");
    setQuestions(updatedQuestions); 
  };
  const handleRemoveAnswer = (questionIndex, answerIndex) => { 
    const updatedQuestions = [...questions];
    if (updatedQuestions[questionIndex].answers.length <= 2) { alert("שאלה חייבת להכיל לפחות 2 תשובות"); return; }
    updatedQuestions[questionIndex].answers.splice(answerIndex, 1);
    if (updatedQuestions[questionIndex].correct === answerIndex) updatedQuestions[questionIndex].correct = 0; 
    else if (updatedQuestions[questionIndex].correct > answerIndex) updatedQuestions[questionIndex].correct--;
    setQuestions(updatedQuestions);

    setFormErrors(prevErrors => {
        const newErrors = JSON.parse(JSON.stringify(prevErrors));
        if (newErrors.questions?.[questionIndex]?.answersGeneral) {
            const currentQ = updatedQuestions[questionIndex];
            if (currentQ.answers.every(a => a && a.trim() !== "")) {
                delete newErrors.questions[questionIndex].answersGeneral;
                if (Object.keys(newErrors.questions[questionIndex]).length === 0) delete newErrors.questions[questionIndex];
                if (Object.keys(newErrors.questions).length === 0) delete newErrors.questions;
            }
        }
        return newErrors;
    });
  };
  const handleCancel = () => onCancel(selectedHospitalId, isNewTopic ? null : selectedTopicId);
  
  const createNewTopic = async () => { 
    try {
      const endpoint = `/api/trainings/training-topics`;
      const topicResponse = await API.post(endpoint, { name: topicName.trim(), hospitalId: selectedHospitalId });
      let newTopicId = topicResponse.data?.topic?._id || topicResponse.data?._id || topicResponse.data?.id;
      if (!newTopicId) throw new Error("Could not find topic ID in response");
      return newTopicId;
    } catch (error) { console.error("Error creating new topic:", error); throw error; }
  };

  const createNewTestTwoStep = async (topicId) => { 
    try {
      const testId = testName.trim().replace(/\s+/g, "-");
      let highestOrder = -1;
      if (Array.isArray(tests) && tests.length > 0) highestOrder = Math.max(...tests.map(test => test.order !== undefined ? test.order : -1));
      
      const basicTestData = { id: testId, title: testName.trim(), order: highestOrder + 1, questions: questions.map(q => ({ question: q.question, answers: q.answers, correct: q.correct, explanation: q.explanation })) };
      // handlingErrorLocally throughout this flow: the failure is rendered by
      // handleSaveTest / the toast below, and the automatic floating error
      // would otherwise say the same thing a second time.
      const createRes = await API.post(`/api/trainings/training-topic/${encodeURIComponent(topicId)}/hospital/${encodeURIComponent(selectedHospitalId)}/test`, basicTestData, { handlingErrorLocally: true });
      
      if (!createRes.data) throw new Error("Empty response from server when creating test");
      let createdTestId = createRes.data.id || createRes.data._id || testId;
      
      const hasImages = Object.keys(questionImages).length > 0 || Object.keys(explanationImages).length > 0;
      if (!hasImages) return createRes.data;
      
      const formData = new FormData();
      formData.append('id', createdTestId); formData.append('title', testName.trim()); formData.append('order', highestOrder + 1);
      formData.append('questions', JSON.stringify(questions)); 
      Object.entries(questionImages).forEach(([index, file]) => { if (index === '0') formData.append("questionImage", file); formData.append(`questionImage_${index}`, file); });
      Object.entries(explanationImages).forEach(([index, file]) => { if (index === '0') formData.append("explanationImage", file); formData.append(`explanationImage_${index}`, file); });
      
      try {
        return (await API.put(`/api/trainings/training-topic/${encodeURIComponent(topicId)}/hospital/${encodeURIComponent(selectedHospitalId)}/test/${encodeURIComponent(createdTestId)}`, formData, { handlingErrorLocally: true })).data;
      } catch (updateError) {
        console.error("Error updating test with images:", updateError);
        // The test itself was created and the caller navigates away on return,
        // so an inline message would unmount before it could be read. The
        // floating toast outlives the navigation — and this is a warning about
        // a partial success, not a failed save.
        const reason = updateError?.serverError?.hebMessage;
        window.showFloatingError?.({
          hebMessage: `המבחן נוצר, אך העלאת התמונות נכשלה${reason ? ` (${reason})` : ''}. פתח את המבחן לעריכה כדי להוסיף אותן שוב.`
        });
        return createRes.data;
      }
    } catch (error) {
      // Rethrown to handleSaveTest, which renders the reason inline — an alert
      // here would say the same thing twice.
      console.error("Error in createNewTestTwoStep:", error);
      throw error;
    }
  };

  const saveTest = async (topicId, isNewTest = false) => { 
    const testIdToSave = isNewTest ? testName.trim().replace(/\s+/g, "-") : editingTestId;
    const formData = new FormData();
    formData.append('id', testIdToSave); formData.append('title', testName.trim());

    if (!isNewTest) {
      const existingTest = tests.find(test => test.id === editingTestId || test._id === editingTestId);
      formData.append('order', (existingTest && existingTest.order !== undefined) ? existingTest.order : 0);
    } else {
      let highestOrder = -1;
      if (Array.isArray(tests) && tests.length > 0) highestOrder = Math.max(...tests.map(test => (test.order !== undefined ? test.order : -1)));
      formData.append('order', highestOrder + 1);
    }

    const cleanQuestions = questions.map((qState, index) => {
      let qImgInfo = null, delQI = true; 
      if (questionImages[index]) {} 
      else if (questionImagePreviews[index] && !questionImagePreviews[index].startsWith('blob:')) { if (qState.questionImage?.fileName) { qImgInfo = qState.questionImage; delQI = false; }}
      let eImgInfo = null, delEI = true; 
      if (explanationImages[index]) {} 
      else if (explanationImagePreviews[index] && !explanationImagePreviews[index].startsWith('blob:')) { if (qState.explanationImage?.fileName) { eImgInfo = qState.explanationImage; delEI = false; }}
      return { question: qState.question || "", answers: qState.answers || ["","","",""], correct: qState.correct ?? 0, explanation: qState.explanation || "", questionImage: qImgInfo, explanationImage: eImgInfo, deleteQuestionImage: delQI, deleteExplanationImage: delEI };
    });

    formData.append('questions', JSON.stringify(cleanQuestions));

    for (let i = 0; i < cleanQuestions.length; i++) {
      if (questionImages[i]) { formData.append(`questionImage_${i}`, questionImages[i]); if (i === 0 && isNewTest) formData.append("questionImage", questionImages[i]); }
      if (explanationImages[i]) { formData.append(`explanationImage_${i}`, explanationImages[i]); if (i === 0 && isNewTest) formData.append("explanationImage", explanationImages[i]); }
    }
    formData.append('fullImageReset', 'true'); 
    const endpoint = isNewTest ? `/api/trainings/training-topic/${encodeURIComponent(topicId)}/hospital/${encodeURIComponent(selectedHospitalId)}/test` : `/api/trainings/training-topic/${encodeURIComponent(topicId)}/hospital/${encodeURIComponent(selectedHospitalId)}/test/${encodeURIComponent(testIdToSave)}`;
    const method = isNewTest ? 'post' : 'put';

    try {
      return (await API[method](endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' }, handlingErrorLocally: true })).data;
    } catch (error) { console.error('Error submitting the form:', error); if (error.response) console.error('Server response:', error.response.data); throw error; }
  };

  const handleEditTestName = () => { 
    setEditedTestName(testName); 
    setIsEditingTitle(true); 
  };

  const handleSaveTestName = async () => {
    if (!editedTestName.trim()) {
      setFormErrors(p => ({ ...p, testName: "שם המבחן לא יכול להיות ריק" }));
      console.log("Setting scrollToRef for testNameEditableInputRef (from handleSaveTestName):", testNameEditableInputRef); // DEBUG
      setScrollToRef(testNameEditableInputRef); 
      return;
    }
    if (editedTestName === testName) { setIsEditingTitle(false); setFormErrors(p=>({...p,testName:undefined})); return; }
    setIsLoading(true); setError(null);
    try { 
      const formData = new FormData();
      formData.append('id', editingTestId);
      formData.append('title', editedTestName.trim());
      formData.append('questions', JSON.stringify(questions.map(q => ({ 
            question: q.question, answers: q.answers, correct: q.correct, explanation: q.explanation,
            questionImage: q.questionImage, explanationImage: q.explanationImage,
      }))));
      await API.put(`/api/trainings/training-topic/${encodeURIComponent(selectedTopicId)}/hospital/${encodeURIComponent(selectedHospitalId)}/test/${encodeURIComponent(editingTestId)}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, handlingErrorLocally: true });
      setTestName(editedTestName.trim());
      setIsEditingTitle(false); 
      setFormErrors(p=>({...p,testName:undefined})); 
    }
    catch (e) { setError(e?.serverError?.hebMessage || 'עדכון שם המבחן נכשל. אנא נסה שנית.'); }
    finally { setIsLoading(false); }
  };

  const handleCancelEditName = () => { 
    setIsEditingTitle(false);
    setEditedTestName(testName);
    setFormErrors(p=>({...p,testName:undefined}));
  };

  const handleSaveTest = async () => {
    setFormErrors({}); 
    let firstErrorRefToSet = null;
    const newErrors = { questions: {} };
    let overallIsValid = true;

    if (!selectedHospitalId) { alert("חסרים נתונים לשמירת המבחן - נדרש לבחור בית חולים"); return; }
    if (isNewTopic && !topicName.trim()) { alert("חסרים נתונים לשמירת המבחן - נדרש להזין שם נושא"); return; }
    if (!isNewTopic && !selectedTopicId) { alert("חסרים נתונים לשמירת המבחן - נדרש לבחור נושא"); return; }
    
    const currentTestNameForValidation = isEditingTitle ? editedTestName : testName;
    if (!currentTestNameForValidation.trim()) {
      overallIsValid = false;
      newErrors.testName = "נדרש להזין שם מבחן";
      if (!firstErrorRefToSet) {
        firstErrorRefToSet = testNameEditableInputRef;
        console.log("Identified firstErrorRefToSet (testName from handleSaveTest):", testNameEditableInputRef); // DEBUG
      }
    }
    
    questions.forEach((q, index) => {
      const questionErrors = {};
      let questionHasError = false;
      if (!q.question || !q.question.trim()) {
        overallIsValid = false; questionHasError = true;
        questionErrors.question = `שאלה ${index + 1} חסרה תוכן`;
        if (!firstErrorRefToSet) {
          firstErrorRefToSet = questionFormRefs.current[index];
           console.log(`Identified firstErrorRefToSet (question ${index} text from handleSaveTest):`, questionFormRefs.current[index]); // DEBUG
        }
      }
      if (!q.answers || q.answers.some(a => !a || !a.trim())) {
        overallIsValid = false; questionHasError = true;
        questionErrors.answersGeneral = `שאלה ${index + 1} מכילה תשובות ריקות`;
        if (!firstErrorRefToSet) {
           firstErrorRefToSet = questionFormRefs.current[index];
           console.log(`Identified firstErrorRefToSet (question ${index} answers from handleSaveTest):`, questionFormRefs.current[index]); // DEBUG
        }
      }
      if (questionHasError) newErrors.questions[index] = questionErrors;
    });
    
    if (!overallIsValid) {
      setFormErrors(newErrors);
      if (firstErrorRefToSet) {
        console.log("Calling setScrollToRef (from handleSaveTest) with:", firstErrorRefToSet); // DEBUG
        setScrollToRef(firstErrorRefToSet);
      } else {
        console.warn("Validation failed but no specific ref was identified to scroll to (from handleSaveTest)."); // DEBUG
      }
      return; 
    }

    setIsLoading(true); setError(null); 
    try {
      if (isNewTopic) { 
        let newTopicId;
        try { newTopicId = await createNewTopic(); } 
        catch (error) { alert("שגיאה ביצירת נושא חדש: " + (error.response?.data?.message || error.message)); setIsLoading(false); return; }
        if (!newTopicId) { alert("שגיאה: לא ניתן ליצור נושא חדש"); setIsLoading(false); return; }
        const testResponse = await createNewTestTwoStep(newTopicId);
        onSuccess({ ...testResponse, topicId: newTopicId, topicName: topicName.trim() });
      } else { 
        const isNew = !editingTestId; 
        const res = isNew ? await createNewTestTwoStep(selectedTopicId) : await saveTest(selectedTopicId, false); 
        onSuccess(res); 
      }
    } catch (e) {
      // Hebrew, and the server's own reason where it has one — "סוג קובץ לא
      // נתמך" tells the author to swap an image; an English stack does not.
      setError(e?.serverError?.hebMessage || 'שמירת המבחן נכשלה. אנא נסה שנית.');
    }
    finally { setIsLoading(false); }
  };

  const stepOneProps = {
    selectedHospital: hospitals.find(h => h._id === selectedHospitalId),
    selectedHospitalId, handleHospitalSelect, isNewTopic, topicName, setTopicName,
    topics, selectedTopicId, handleTopicSelect, topicsLoading, handleNextStep, handleCancel
  };

  const stepTwoProps = {
    selectedHospital: hospitals.find(h => h._id === selectedHospitalId),
    selectedTopic: isNewTopic ? { name: topicName } : topics.find(t => t.id === selectedTopicId || t._id === selectedTopicId),
    isNewTopic, testName, setTestName: setTestNameProxy,
    isLoading, handlePreviousStep, handleNextStep, formErrors
  };

  const stepThreeProps = {
    selectedTest: tests.find(test => test.id === editingTestId || test._id === editingTestId),
    editingTestId, testName, isEditingTitle, editedTestName, setEditedTestName: setEditedTestNameProxy,
    handleEditTestName, handleSaveTestName, handleCancelEditName,
    handleCancel, handlePreviousStep, questions, handleAddQuestion, handleQuestionChange, handleDeleteQuestion,
    handleAddAnswer, handleRemoveAnswer,
    imageData: { questionImagePreviews, explanationImagePreviews, onQuestionImageChange: handleQuestionImageChange, onExplanationImageChange: handleExplanationImageChange, onRemoveQuestionImage: handleRemoveQuestionImage, onRemoveExplanationImage: handleRemoveExplanationImage },
    handleSaveTest, isLoading, formErrors,
    testNameEditableInputRef, 
    getQuestionFormRef: (index) => questionFormRefs.current[index], 
  };

  const renderStepIndicators = () => { if (!!editingTestId || initialTestName) return null; return <TrainingSteps currentStep={currentStep} />; };

  return (
    <div className="training-creation-workflow">
      <div className="training-header-main">
        <h2 className="training-header-title">
          {editingTestId ? 'עריכת מבחן' : (isNewTopic ? 'יצירת נושא ומבחן חדשים' : 'ניהול שאלות אימון')}
        </h2>
        <div ref={errorRef}>
          {/* autoDismissMs=0: a failed save stays until the user acts on it. */}
          <UploadErrors
            messages={error ? [error] : []}
            onDismiss={() => setError(null)}
            autoDismissMs={0}
          />
        </div>
      </div>
      {renderStepIndicators()}
      <div className="training-form-container">
        {currentStep === 1 && <StepOne {...stepOneProps} />}
        {currentStep === 2 && <StepTwo {...stepTwoProps} />}
        {currentStep === 3 && <StepThree {...stepThreeProps} />}
      </div>
      <FloatingButton icon={ChevronUp} color="16, 60, 110" ariaLabel="Scroll to top of page" className="high-priority" scrollToTop={true} />
    </div>
  );
};

export default TrainingCreation;